// Tests run from the agent's browser, not from the portal server. No SIP payloads
// or credentials are sent, logged, or included in the diagnostic report.
export function wssTarget(value) {
  let url;
  try { url = new URL(value); } catch { throw Error('Enter a valid secure WebSocket URL'); }
  if (url.protocol !== 'wss:' || url.username || url.password || url.search || url.hash || url.pathname === '/')
    throw Error('Use a WSS URL with a path and without credentials, query parameters or fragments');
  return url;
}

export function testPBXConnection(value, {signal, timeoutMs = 12000, env = globalThis} = {}) {
  const started = Date.now();
  return new Promise(resolve => {
    let socket, timer, errorTimer, target, finished = false;
    const finish = (status, message, extra = {}) => {
      if (finished) return;
      finished = true;
      clearTimeout(timer); clearTimeout(errorTimer);
      env.document?.removeEventListener('securitypolicyviolation', policy);
      signal?.removeEventListener('abort', abort);
      if (socket) {
        socket.onopen = socket.onerror = socket.onclose = null;
        try { socket.close(); } catch {}
      }
      resolve({at: new Date(started).toISOString(), elapsed_ms: Date.now() - started,
        target: target?.href || '', status, message, ...extra});
    };
    const abort = () => finish('cancelled', 'Connection test cancelled.');
    const policy = event => {
      if (event.disposition === 'report') return;
      let blocked;
      try { blocked = new URL(event.blockedURI); } catch { return; }
      if (blocked.origin === target.origin && ['connect-src', 'default-src'].includes(event.effectiveDirective))
        finish('browser_policy', 'The portal Content Security Policy blocked this PBX. Ask the administrator to allow this WSS origin in connect-src.');
    };
    try { target = wssTarget(value); } catch (error) { finish('invalid_url', error.message); return; }
    if (signal?.aborted) { abort(); return; }
    if (env.isSecureContext === false) { finish('insecure_page', 'Open the portal over trusted HTTPS. WebRTC microphone access requires a secure page.'); return; }
    if (typeof env.WebSocket !== 'function') { finish('unsupported', 'This browser does not support WebSocket connections.'); return; }
    env.document?.addEventListener('securitypolicyviolation', policy);
    signal?.addEventListener('abort', abort, {once:true});
    timer = setTimeout(() => finish('timeout', 'No SIP WebSocket upgrade within 12 seconds. Check workstation DNS/VPN, TCP reachability and the PBX HTTPS service.'), timeoutMs);
    try {
      socket = new env.WebSocket(target.href, 'sip');
      socket.onopen = () => socket.protocol === 'sip'
        ? finish('passed', 'TLS and the SIP WebSocket upgrade succeeded from this workstation. SIP login and audio have not been tested.', {subprotocol:'sip'})
        : finish('wrong_protocol', 'The server did not negotiate the SIP WebSocket protocol. Check the /ws path and the PBX PJSIP WebSocket module.');
      socket.onerror = () => {
        // CSP events can arrive after the generic socket error. Do not claim a
        // DNS, TLS or firewall diagnosis that the browser did not reveal.
        errorTimer = setTimeout(() => finish('wss_failed', 'The browser could not open the SIP WebSocket. It hides the exact DNS/TLS/firewall/HTTP cause; check the certificate link and Developer Tools → Network → WS.'), 100);
      };
      socket.onclose = event => {
        if (errorTimer) return;
        errorTimer = setTimeout(() => finish('closed', 'The socket closed before a SIP WebSocket upgrade. Check the PBX listener, certificate and proxy settings.', {close_code: event.code}), 100);
      };
    } catch (error) {
      finish(error.name === 'SecurityError' ? 'browser_policy' : 'wss_failed',
        error.name === 'SecurityError' ? 'The browser blocked this WebSocket. Inspect the browser security policy and Developer Tools.' : 'The browser could not create the secure WebSocket connection.');
    }
  });
}

export function keypadDiagnosticReport(phone,{profileID='',phoneAllowed,permission,approval}={}) {
  return {schema:1,portal_version:'0.76.2-beta.1',generated_at:new Date().toISOString(),
    access:{profile_id:profileID,phone_allowed:typeof phoneAllowed==='boolean'?phoneAllowed:null,
      phone_permission:typeof permission==='boolean'?permission:null,approval_state:approval||'unknown'},
    media:phone.keypadDetails?.()||null,
    note:'On-demand browser snapshot. No PIN digits, tone buffer, credentials, raw SIP or SDP. Native DTMF availability is not a delivery receipt from the PBX.'};
}

export function phoneDiagnosticReport(sip, phone, probe, env = globalThis) {
  let target = '';
  try { target = wssTarget(sip.websocket).href; } catch {}
  return {
    schema: 4, portal_version: '0.76.2-beta.1', generated_at: new Date().toISOString(),
    assignment: {profile_id: sip.profile_id || '', domain: sip.domain || '', endpoint: sip.username || '',
      websocket: target, network_mode: sip.network_mode || 'public', audio_recovery: phone.audioRecoveryMode || sip.audio_recovery || 'off'},
    browser: {secure_context: env.isSecureContext === true, websocket_supported: typeof env.WebSocket === 'function',
      webrtc_supported: typeof env.RTCPeerConnection === 'function', microphone_api: !!env.navigator?.mediaDevices?.getUserMedia},
    connection_test: probe || null, registration: phone.diagnostics?.() || null, keypad:phone.keypadDetails?.()||null,
    note: 'Browser-to-PBX test. No SIP password, TURN credentials, raw SIP messages, SDP or cookies are included. Call IDs and structured call events are included. A successful WSS test does not verify SIP registration or audio.'
  };
}

export function bindPhoneDiagnostics({modal, sip, phone, download, canDebug=()=>false}) {
  const test = modal.querySelector('[data-pbx-test]');
  const output = modal.querySelector('[data-pbx-test-result]');
  const save = modal.querySelector('[type="submit"]');
  let controller, probe;
  test.onclick = async () => {
    if (controller) return;
    if (phone.session || phone.connecting || phone.registered) {
      output.textContent = 'Disconnect the phone and finish active calls before running the connection test.';
      return;
    }
    controller = new AbortController();
    test.disabled = save.disabled = true;
    output.textContent = 'Testing this workstation → PBX secure WebSocket…';
    try {
      probe = await testPBXConnection(sip.websocket, {signal: controller.signal});
      output.textContent = probe.message;
      output.classList.toggle('error', !['passed', 'cancelled'].includes(probe.status));
    } finally { controller = null; test.disabled = save.disabled = false; }
  };
  const report=modal.querySelector('[data-pbx-report]');
  if(report)report.onclick=()=>{if(!canDebug()||!phone.debugActive?.()){output.textContent='An administrator must enable Call debug before downloading a log.';return;}download('tyme-phone-diagnostics.json',JSON.stringify(phoneDiagnosticReport(sip,phone,probe),null,2),'application/json');};
  const link = modal.querySelector('[data-pbx-certificate]');
  try { const url = wssTarget(sip.websocket); url.protocol = 'https:'; link.href = url.href; }
  catch { link.hidden = true; }
  modal.addEventListener('close', () => controller?.abort(), {once: true});
}
